import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import api from "./api";

import LoginPage from "./components/LoginPage";
import Header from "./components/Header";
import ProjectDashboardPage from "./components/ProjectDashboardPage"; 
import UserDashboardPage from "./components/UserDashboardPage"; 
import VendorDashboardPage from "./components/VendorDashboardPage"; 
import AuditDashboardPage from "./components/AuditDashboardPage"; 

import QrStaticPage from "./components/QrStaticPage"; 
import OverviewPage from "./components/OverviewPage"; 
import TestSmsPage from "./components/SMSTestPage"; 
import EmailTestPage from "./components/EmailTestPage"; 

import UserRegistrationPage from "./components/UserRegistrationPage";
import CreateProjectPage from "./components/CreateProjectPage";
import CreateVoucherPage from "./components/CreateVoucherPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import InitiateProjectPage from "./components/InitiateProjectPage";
import BeneficiaryListPage from "./components/UploadBeneficiaryListPage";
import ApproveBeneficiaryListPage from "./components/ApproveBeneficiaryListPage";
import UploadBeneficiaryListPage from "./components/UploadBeneficiaryListPage";
import RegisterCoordinatorPage from "./components/RegisterCoordinatorPage";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/main.css";

// --- Nav config ---
const navConfig = {
  ADMIN: {
    nav: ["new-coordinator", "initiate-project", "dashboard", "user-dashboard", "audit-dashboard", "overview"], 
    defaultRoute: "/overview", 
  },
  "PROJECT COORDINATOR": {
    nav: ["user-registration", "create-project", "dashboard", "overview"], 
    defaultRoute: "/overview", 
  },
  ISSUER: {
    nav: ["create-voucher", "overview"],
    defaultRoute: "/overview", 
  },
  OBSERVER: {
    nav: ["dashboard", "user-dashboard", "overview"], 
    defaultRoute: "/overview", 
  },
  CHECKER: {
    nav: ["approve-beneficiary-list", "overview"], 
    defaultRoute: "/overview", 
  },
  MAKER: {
    nav: [
      "beneficiary-list",
      "approve-beneficiary-list", 
      // Removed duplicate "upload-beneficiary-list" here
      "overview",
    ],
    defaultRoute: "/overview",
  },
  VENDOR: {
    nav: ["vendor-dashboard"],
    defaultRoute: "/vendor-dashboard",
  }
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [projects, setProjects] = useState([]); 
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("currentUser");
    const storedToken = localStorage.getItem("jwtToken");

    if (storedUser && storedToken) {
      const user = JSON.parse(storedUser);
      const storedProjectId = localStorage.getItem("selectedProjectId");
      if (storedProjectId) {
        user.selectedProjectId = parseInt(storedProjectId, 10);
      }
      setCurrentUser(user);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      // ✅ Optimization: Only fetch projects if role is NOT VENDOR
      if (currentUser.role !== "VENDOR") {
        api
          .get("/vres/projects")
          .then((res) => {
            setProjects(res.data || []);
          })
          .catch((err) => {
              console.error("Error fetching projects:", err);
              // Suppress toast on 403 (Observers might not have access to all projects list)
              if (err.response && err.response.status !== 403) {
                  toast.error("Could not load project list.");
              }
          });
      }
    }
  }, [currentUser]);

  // --- Login Handler ---
  const handleLogin = (e, navigate) => {
    e.preventDefault();
    const loginPayload = {
      userId: e.target.username.value,
      password: e.target.password.value,
    };

    api
      .post("/vres/auth/login", loginPayload)
      .then((res) => {
        const userData = res.data;
        console.log("userData from login:", userData); 

        if (!userData.jwtToken) {
          toast.error("Login failed: No token received.");
          return;
        }
        localStorage.setItem("jwtToken", userData.jwtToken);

        const role = userData.role;
        const config = navConfig[role];

        if (!config) {
          toast.error(`Login failed: No UI configuration for role "${role}".`);
          localStorage.removeItem("jwtToken");
          return;
        }

        const user = {
          ...userData,
          nav: config.nav
        };

        // --- AUTO PROJECT & DEPARTMENT SELECTION LOGIC ---
        if (userData.projects && userData.projects.length > 0) {
          const firstProject = userData.projects[0];
          const defaultProjectId = firstProject.id || firstProject.projectId;
          
          if (defaultProjectId) {
            localStorage.setItem("selectedProjectId", defaultProjectId);
            user.selectedProjectId = defaultProjectId;
          }

          const deptId = firstProject.departmentId || userData.departmentId;
          
          if (deptId) {
            localStorage.setItem("departmentId", deptId);
            user.departmentId = deptId;
          } else {
            console.warn("Department ID not found in login response");
          }
        }

        localStorage.setItem("currentUser", JSON.stringify(user));
        setCurrentUser(user);
        toast.success(`Welcome ${user.name}!`);

        navigate(config.defaultRoute); 
      })
      .catch((err) => {
        console.error("Login error:", err);
        const errorMessage =
          err.response?.data?.message ||
          "Invalid credentials. Please try again.";
        toast.error(errorMessage);
      });
  };

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("selectedProjectId");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("departmentId");
    setCurrentUser(null);
    toast.info("You have been logged out.");
  };

  return (
    <>
      <Router>
        {currentUser ? (
          <div className="layout">
            <Header
              currentUser={currentUser}
              handleLogout={handleLogout}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
            <main
              className="main-content"
              style={{
                marginLeft: sidebarOpen ? 220 : 60,
                transition: "margin-left 0.3s",
                padding: 20,
              }}
            >
              <Routes>
                <Route path="/overview" element={<OverviewPage />} />
                
                <Route
                  path="/dashboard"
                  element={
                      <ProjectDashboardPage 
                        currentUser={currentUser} 
                        allProjects={projects} 
                      />
                  }
                />
                
                <Route path="/user-dashboard" element={<UserDashboardPage />} />
                <Route path="/vendor-dashboard" element={<VendorDashboardPage />} />

                {/* ✅ ADDED AUDIT DASHBOARD ROUTE */}
                <Route path="/audit-dashboard" element={<AuditDashboardPage />} />

                <Route path="/qr" element={<QrStaticPage />} />
                <Route path="/test-sms" element={<TestSmsPage />} />
                <Route path="/test-email" element={<EmailTestPage />} />

                <Route
                  path="/user-registration"
                  element={<UserRegistrationPage />}
                />
                <Route
                  path="/create-project"
                  element={
                    <CreateProjectPage 
                        currentUser={currentUser} 
                        setProjects={setProjects} 
                    />
                  }
                />
                <Route
                  path="/new-coordinator"
                  element={<RegisterCoordinatorPage />}
                />
                <Route
                  path="/create-voucher"
                  element={<CreateVoucherPage currentUser={currentUser} />}
                />
                
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path="/reset-password"
                  element={<ResetPasswordPage />}
                />
                <Route
                  path="/initiate-project"
                  element={<InitiateProjectPage />}
                />
                <Route
                  path="/beneficiary-list"
                  element={
                    <BeneficiaryListPage
                      readOnly={currentUser?.role === "CHECKER"}
                    />
                  }
                />
                <Route
                  path="/approve-beneficiary-list"
                  element={
                    <ApproveBeneficiaryListPage
                      role={
                        currentUser?.role ? currentUser.role.toLowerCase() : ""
                      }
                      projectId={currentUser?.selectedProjectId}
                    />
                  }
                />
                <Route
                  path="/upload-beneficiary-list"
                  element={<UploadBeneficiaryListPage />}
                />

                <Route
                  path="*"
                  element={
                    <Navigate
                      to={navConfig[currentUser.role]?.defaultRoute || "/overview"}
                      replace
                    />
                  }
                />
              </Routes>
            </main>
          </div>
        ) : (
          <Routes>
            <Route
              path="/login"
              element={<LoginPage handleLogin={handleLogin} />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />
            <Route path="/qr" element={<QrStaticPage />} />
            <Route path="/test-sms" element={<TestSmsPage />} />
            <Route path="/test-email" element={<EmailTestPage />} />
            
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
      </Router>
      {/* ✅ UPDATED: Changed position to top-center for global centering */}
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </>
  );
}

export default App;